/**
 * Smart caching system with LRU eviction strategy
 * Prevents memory leaks while maintaining performance
 */

export interface CacheOptions {
    maxEntries?: number;
    ttl?: number; // Time to live in milliseconds
}

export class LRUCache<K, V> {
    private cache = new Map<K, { value: V; timestamp: number }>();
    private readonly maxEntries: number;
    private readonly ttl: number | null;

    constructor(options: CacheOptions = {}) {
        this.maxEntries = options.maxEntries ?? 1000;
        this.ttl = options.ttl ?? null;
    }

    /**
     * Get a value from cache or generate it using the provided function
     */
    get(key: K, generator?: () => V): V | undefined {
        const entry = this.cache.get(key);

        if (entry) {
            // Check if entry has expired
            if (this.ttl && Date.now() - entry.timestamp > this.ttl) {
                this.cache.delete(key);
            } else {
                // Move to end (most recently used)
                this.cache.delete(key);
                this.cache.set(key, entry);
                return entry.value;
            }
        }

        if (generator) {
            const value = generator();
            this.set(key, value);
            return value;
        }

        return undefined;
    }

    /**
     * Set a value in the cache
     */
    set(key: K, value: V): void {
        // Remove oldest entry if at capacity
        if (this.cache.size >= this.maxEntries && !this.cache.has(key)) {
            const firstKey = this.cache.keys().next().value;
            if (firstKey !== undefined) {
                this.cache.delete(firstKey);
            }
        }

        this.cache.set(key, {
            value,
            timestamp: Date.now(),
        });
    }

    /**
     * Check if a key exists in the cache
     */
    has(key: K): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;

        // Check expiration
        if (this.ttl && Date.now() - entry.timestamp > this.ttl) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    /**
     * Clear all entries from the cache
     */
    clear(): void {
        this.cache.clear();
    }

    /**
     * Get the current size of the cache
     */
    get size(): number {
        return this.cache.size;
    }

    /**
     * Get cache statistics
     */
    getStats(): { size: number; maxEntries: number; fillRate: number } {
        return {
            size: this.cache.size,
            maxEntries: this.maxEntries,
            fillRate: this.cache.size / this.maxEntries,
        };
    }
}

/**
 * Duration cache for ISO 8601 duration strings
 */
export class DurationCache extends LRUCache<number, string> {
    constructor() {
        super({ maxEntries: 500 }); // Smaller cache for durations
    }

    /**
     * Convert seconds to ISO 8601 duration format with caching
     */
    convertToIsoDuration(seconds: number): string {
        return (
            this.get(seconds, () => {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                const secs = Math.floor(seconds % 60);

                let isoDuration = 'PT';
                if (hours > 0) isoDuration += `${hours}H`;
                if (minutes > 0) isoDuration += `${minutes}M`;
                if (secs > 0) isoDuration += `${secs}S`;

                return isoDuration === 'PT' ? 'PT0S' : isoDuration;
            }) ?? 'PT0S'
        );
    }
}
